export const SITE_URL = "https://www.bonobohope.org";
export const SITE_NAME = "Bonobo Hope Initiative";
export const DEFAULT_IMAGE = "/KanziSlideshow/KanziAndKeyboard2.jpg";

const absoluteUrl = (path) => `${SITE_URL}${path}`;

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "NGO",
  name: SITE_NAME,
  alternateName: "BHI",
  url: SITE_URL,
  logo: absoluteUrl("/bonobo-hope-logo.png"),
  email: "info@bonobohope.org",
  description:
    "Bonobo Hope Initiative works to restore and continue lexigram-based bonobo and chimpanzee language research while supporting education, conservation, and bonobo-centered care.",
};

const researchTopicSeo = {
  lana: {
    title: "Lana | LANA Project and Chimpanzee Language Research",
    description:
      "Learn about Lana, the chimpanzee at the center of the LANA Project, and how Yerkish, lexigrams, and computer-mediated research shaped ape language studies.",
    image: "/KeyboardP1_6.png",
    about: ["Lana", "LANA Project", "Chimpanzee language research"],
  },
  "sherman-and-austin": {
    title: "Sherman and Austin | Chimpanzee Symbolic Communication",
    description:
      "Explore Sherman and Austin's lexigram studies, food-message tasks, symbolic reference, and their role in the ape language research trajectory.",
    image: "/KanziSlideshow/KanziPointsToLexigram.jpg",
    about: ["Sherman and Austin", "Symbolic communication", "Lexigrams"],
  },
  yerkish: {
    title: "Yerkish | Lexigram Language of the LANA Project",
    description:
      "Understand Yerkish, the artificial language designed for the LANA Project to study lexigrams, grammar, and chimpanzee communication.",
    image: "/KeyboardP1_6.png",
    about: ["Yerkish", "LANA Project", "Ernst von Glasersfeld"],
  },
  lexigrams: {
    title: "Lexigrams | Symbolic Language in Ape Research",
    description:
      "Learn how abstract lexigram symbols connected Lana, Sherman and Austin, Kanzi, bonobos, and the broader ape language research trajectory.",
    image: "/KeyboardP1_6.png",
    about: ["Lexigrams", "Symbolic communication", "Ape language"],
  },
  yerkes: {
    title: "Yerkes Primate Center | LANA Project Research Setting",
    description:
      "Explore the Yerkes research environment where Lana, Yerkish, lexigram keyboards, and early chimpanzee language research developed.",
    image: "/KeyboardP1_6.png",
    about: ["Yerkes Primate Center", "LANA Project", "Ape language research"],
  },
  kanzi: {
    title: "Kanzi Research | Bonobo Language, Lexigrams, and Culture",
    description:
      "Study Kanzi's language comprehension, lexigram use, observational learning, tool work, and role in bonobo language research.",
    image: "/KanziHeadshot.png",
    about: ["Kanzi", "Bonobo language research", "Lexigrams"],
  },
  bonobos: {
    title: "Bonobos in Language Research | Culture, Cognition, and Communication",
    description:
      "Explore how bonobos shaped ape language research through development, spoken comprehension, culture, lexigrams, and ethical questions.",
    image: "/KanziSlideshow/GroupPhoto.jpg",
    about: ["Bonobos", "Pan paniscus", "Bonobo communication"],
  },
  pan: {
    title: "Pan | Chimpanzees, Bonobos, Language, and Culture",
    description:
      "Understand Pan as the broader research frame connecting chimpanzees, bonobos, lexigrams, tool culture, communication, and moral responsibility.",
    image: "/KanziSlideshow/KanziFloorLexigrams.jpg",
    about: ["Pan", "Chimpanzees", "Bonobos"],
  },
};

const buildArticleSchema = ({ path, title, description, image, about = [] }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: title.split(" | ")[0],
  description,
  image: absoluteUrl(image),
  author: organizationSchema,
  publisher: organizationSchema,
  mainEntityOfPage: absoluteUrl(path),
  about: about.map((name) => ({ "@type": "Thing", name })),
});

export const seoPages = {
  "/": {
    title: "Bonobo Hope Initiative | Bonobo Language Research and Kanzi",
    description:
      "Help restore bonobo and chimpanzee language research built around Kanzi, lexigrams, culture, and interspecies communication.",
    image: "/KanziSlideshow/KanziAndKeyboard2.jpg",
    schema: organizationSchema,
  },
  "/kanzi": {
    title: "Celebrating Kanzi | Bonobo Hope Initiative",
    description:
      "Celebrate the life of Kanzi, the bonobo whose language abilities, relationships, and lexigram work reshaped understanding of mind and communication.",
    image: "/KanziHeadshot.png",
    schema: {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      name: "Celebrating the Life of Kanzi",
      description:
        "A memorial page and video honoring Kanzi's life, linguistic understanding, and relationships.",
      thumbnailUrl: absoluteUrl("/KanziHeadshot.png"),
      uploadDate: "2025-03-18",
      contentUrl:
        "https://bhivideos.s3.us-east-2.amazonaws.com/KanziMemories1a.mp4",
      publisher: organizationSchema,
    },
  },
  "/bonobos": {
    title: "Bonobos | Intelligence, Culture, and Communication",
    description:
      "Explore bonobos, their social intelligence, emotional lives, communication abilities, and why bonobo language research matters.",
    image: "/KanziSlideshow/GroupPhoto.jpg",
    schema: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Bonobos, Intelligence, Culture, and Communication",
      description:
        "An introduction to bonobos and the scientific importance of bonobo communication and language research.",
      image: absoluteUrl("/KanziSlideshow/GroupPhoto.jpg"),
      author: organizationSchema,
      publisher: organizationSchema,
      mainEntityOfPage: absoluteUrl("/bonobos"),
      about: [
        { "@type": "Thing", name: "Bonobos" },
        { "@type": "Thing", name: "Pan paniscus" },
        { "@type": "Thing", name: "Great apes" },
      ],
    },
  },
  "/bonobo-language-research": {
    title: "Bonobo Language Research | Lexigrams, Kanzi, and Ape Communication",
    description:
      "Understand the bonobo and chimpanzee language research trajectory involving Kanzi, lexigrams, spoken English comprehension, and cultural learning.",
    image: "/KanziSlideshow/KanziPointsToLexigram.jpg",
    schema: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Bonobo Language Research",
      description:
        "A focused overview of lexigram-based bonobo and chimpanzee language research and why BHI is working to restore it.",
      image: absoluteUrl("/KanziSlideshow/KanziPointsToLexigram.jpg"),
      author: organizationSchema,
      publisher: organizationSchema,
      mainEntityOfPage: absoluteUrl("/bonobo-language-research"),
      about: [
        { "@type": "Thing", name: "Bonobo language research" },
        { "@type": "Thing", name: "Ape language" },
        { "@type": "Thing", name: "Interspecies communication" },
      ],
    },
  },
  "/lexigrams": {
    title: "Lexigrams | Kanzi's Symbolic Language Keyboard",
    description:
      "Learn what lexigrams are, how Kanzi used them, and why symbolic keyboards matter for bonobo language research.",
    image: "/KeyboardP1_6.png",
    schema: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Lexigrams and Kanzi's Symbolic Language Keyboard",
      description:
        "An explanation of lexigrams, symbolic communication, and the keyboard systems used in bonobo language research.",
      image: absoluteUrl("/KeyboardP1_6.png"),
      author: organizationSchema,
      publisher: organizationSchema,
      mainEntityOfPage: absoluteUrl("/lexigrams"),
      about: [
        { "@type": "Thing", name: "Lexigrams" },
        { "@type": "Thing", name: "Symbolic communication" },
        { "@type": "Thing", name: "Kanzi" },
      ],
    },
  },
  "/keyboard": {
    title: "Bonobo Hope Keyboard | Kanzi's Lexigram Keyboard",
    description:
      "Use the restored Bonobo Hope lexigram keyboard, hear symbols spoken aloud, and learn how Kanzi and bonobos used abstract symbols for communication.",
    image: "/KeyboardP1_6.png",
  },
  "/music": {
    title: "Bonobo Magical Music | Interactive Bonobo Music Apps",
    description:
      "Explore Bonobo Magical Music, including an interactive piano keyboard and a music-box listening app connected to Bonobo Hope's learning tools.",
    image: "/Keys/Music.jpg",
    schema: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Bonobo Magical Music",
      applicationCategory: "EducationalApplication",
      operatingSystem: "Web",
      description:
        "Two interactive sound tools from Bonobo Hope: a playable magical keyboard and a music-box listening app.",
      url: absoluteUrl("/music"),
      publisher: organizationSchema,
    },
  },
  "/research": {
    title: "Bonobo Language Research | Research Trajectory",
    description:
      "Explore a footnoted research trajectory synthesized from Bonobo Hope's PDF archive on Lana, Yerkish, lexigrams, Sherman and Austin, Kanzi, bonobos, tool culture, and Pan ethics.",
    image: "/KanziSlideshow/KanziPointsToLexigram.jpg",
    schema: buildArticleSchema({
      path: "/research",
      title: "Bonobo Language Research | Research Trajectory",
      description:
        "A footnoted research trajectory on Lana, Yerkish, lexigrams, Sherman and Austin, Kanzi, bonobos, tool culture, and Pan ethics.",
      image: "/KanziSlideshow/KanziPointsToLexigram.jpg",
      about: [
        "Bonobo language research",
        "Kanzi",
        "Lexigrams",
        "Yerkish",
        "LANA Project",
      ],
    }),
  },
  "/research/ernst-von-glasersfeld": {
    title: "Ernst von Glasersfeld | Yerkish, Lexigrams, and the LANA Project",
    description:
      "Learn how Ernst von Glasersfeld's Yerkish language, lexigrams, and correlational grammar shaped the original LANA Project and ape language research.",
    image: "/KeyboardP1_6.png",
    schema: {
      "@context": "https://schema.org",
      "@type": "Person",
      name: "Ernst von Glasersfeld",
      description:
        "Designer of Yerkish and lexigram grammar for the LANA Project, central to the methodology of early ape language research.",
      mainEntityOfPage: absoluteUrl("/research/ernst-von-glasersfeld"),
      sameAs: ["https://en.wikipedia.org/wiki/Ernst_von_Glasersfeld"],
    },
  },
  "/sue-savage-rumbaugh": {
    title: "Sue Savage-Rumbaugh | Kanzi, Bonobos, and Ape Language Research",
    description:
      "Learn about Dr. Sue Savage-Rumbaugh's work with Kanzi, bonobos, chimpanzees, lexigrams, spoken language comprehension, and ape language research.",
    image: "/sue-savage-rumbaugh.jpg",
    schema: {
      "@context": "https://schema.org",
      "@type": "Person",
      name: "Sue Savage-Rumbaugh",
      alternateName: ["E. Sue Savage-Rumbaugh", "Susan Savage-Rumbaugh"],
      image: absoluteUrl("/sue-savage-rumbaugh.jpg"),
      description:
        "Experimental psychologist and primatologist known for ape language research with Kanzi, bonobos, chimpanzees, lexigrams, and spoken language comprehension.",
      affiliation: organizationSchema,
      knowsAbout: [
        "Bonobo language research",
        "Ape language",
        "Lexigrams",
        "Kanzi",
        "Chimpanzee communication",
      ],
      mainEntityOfPage: absoluteUrl("/sue-savage-rumbaugh"),
    },
  },
  "/video": {
    title: "Bonobo Language Research Videos | Kanzi and Ape Communication",
    description:
      "Watch videos documenting Kanzi, bonobo language research, lexigrams, Panbanisha, Sue Savage-Rumbaugh, and ape communication.",
    image: "/KanziSlideshow/KanziPointsToLexigram.jpg",
  },
  "/SueBiblio": {
    title: "Sue Savage-Rumbaugh Bibliography | Ape Language Research",
    description:
      "Browse selected publications by and about Dr. Sue Savage-Rumbaugh, Kanzi, bonobos, chimpanzees, and ape language research.",
    image: "/sue-savage-rumbaugh.jpg",
  },
  "/donate": {
    title: "Donate to Bonobo Hope | Restore Language Research",
    description:
      "Support Bonobo Hope Initiative's work to restore bonobo and chimpanzee language research, public education, and research access.",
    image: "/KanziSlideshow/KanziFloorLexigrams.jpg",
  },
  "/privacy-policy": {
    title: "Privacy Policy | Bonobo Hope Initiative",
    description:
      "Read Bonobo Hope Initiative's privacy policy for donations, payments, analytics, cookies, and personal information.",
    image: "/bonobo-hope-logo.png",
  },
  "/store": {
    title: "Bonobo Hope Store | Support Bonobo Research",
    description:
      "Shop Bonobo Hope merchandise and support bonobo language research, education, and advocacy.",
    image: "/bonobo-hope-logo.png",
  },
};

export const sitemapRoutes = [
  "/",
  "/kanzi",
  "/bonobos",
  "/bonobo-language-research",
  "/lexigrams",
  "/keyboard",
  "/music",
  "/research",
  "/research/lana",
  "/research/sherman-and-austin",
  "/research/yerkish",
  "/research/lexigrams",
  "/research/yerkes",
  "/research/kanzi",
  "/research/bonobos",
  "/research/pan",
  "/research/ernst-von-glasersfeld",
  "/sue-savage-rumbaugh",
  "/video",
  "/SueBiblio",
  "/donate",
  "/store",
  "/privacy-policy",
];

export const getSeoForPath = (pathname) => {
  if (seoPages[pathname]) return seoPages[pathname];
  if (pathname.startsWith("/research/")) {
    const slug = pathname.replace("/research/", "");
    const topicSeo = researchTopicSeo[slug];

    if (topicSeo) {
      return {
        ...topicSeo,
        schema: buildArticleSchema({
          path: pathname,
          title: topicSeo.title,
          description: topicSeo.description,
          image: topicSeo.image,
          about: topicSeo.about,
        }),
      };
    }

    return {
      title: "Bonobo Language Research | Research Wiki",
      description:
        "Explore Bonobo Hope's research wiki on ape language, lexigrams, Yerkish, Kanzi, bonobos, Pan culture, and the restoration of the research trajectory.",
      image: "/KanziSlideshow/KanziPointsToLexigram.jpg",
    };
  }
  if (pathname.startsWith("/history")) {
    return {
      title: "Bonobo Hope History | Kanzi and Language Research Timeline",
      description:
        "Explore the historical timeline of Bonobo Hope, Kanzi, lexigram research, and ape language studies.",
      image: "/KanziSlideshow/KanziAndSue.jpg",
    };
  }
  return {
    title: `${SITE_NAME} | Bonobos, Kanzi, and Language Research`,
    description:
      "Bonobo Hope Initiative supports bonobo language research, education, conservation, and the restoration of Kanzi's research trajectory.",
    image: DEFAULT_IMAGE,
  };
};

export const getCanonicalPath = (pathname) => {
  if (pathname === "/KanziCelebration") return "/kanzi";
  if (pathname === "/wiki" || pathname.startsWith("/wiki/")) {
    return pathname.replace(/^\/wiki/, "/research");
  }
  if (pathname === "/BonoboHopeKeyboard") return "/keyboard";
  if (pathname === "/SueBiblio") return "/SueBiblio";
  return pathname.replace(/\/$/, "") || "/";
};
